export const normalizeFrequencyKey = (value) => {
  if (!value) {
    return null;
  }

  const normalized = String(value).trim().toLowerCase();

  if (normalized === 'monthly' || normalized === 'month') {
    return 'monthly';
  }

  if (
    normalized === 'yearly' ||
    normalized === 'annual' ||
    normalized === 'year'
  ) {
    return 'yearly';
  }

  if (normalized === 'lifetime') {
    return 'lifetime';
  }

  if (
    normalized === 'one-time' ||
    normalized === 'one_time' ||
    normalized === 'onetime' ||
    normalized === 'one time'
  ) {
    return 'one_time';
  }

  return normalized;
};

export const getDisplayFrequency = (frequencyKey) => {
  const key = normalizeFrequencyKey(frequencyKey);

  if (key === 'monthly') {
    return 'Monthly';
  }

  if (key === 'yearly') {
    return 'Yearly';
  }

  if (key === 'lifetime') {
    return 'Lifetime';
  }

  if (key === 'one_time') {
    return 'One-Time';
  }

  return frequencyKey;
};

export const getFrequencySuffix = (frequencyValue) => {
  const key = normalizeFrequencyKey(frequencyValue);

  if (key === 'monthly') {
    return '/ Month';
  }

  if (key === 'yearly') {
    return '/ Year';
  }

  return '/ One-time';
};

const mapOptionFromNewShape = (plan, option) => {
  const frequency = normalizeFrequencyKey(
    option?.frequency || option?.display_frequency
  );

  return {
    id: option?.id,
    plan_id: plan?.id,
    plan_name: plan?.name,
    package_id: option?.package_id,
    package_name: option?.package_name,
    package_description: option?.package_description,
    frequency,
    display_frequency:
      option?.display_frequency || getDisplayFrequency(frequency),
    interval_count: option?.interval_count ?? null,
    interval_unit: option?.interval_unit ?? null,
    price: Number(option?.price ?? 0),
    activation_fees: option?.activation_fees,
    is_prepaid: Boolean(option?.is_prepaid),
    is_recurring: Boolean(option?.is_recurring),
    status: option?.status,
    description: option?.description,
    stripe_price_id: option?.stripe_price_id,
  };
};

const mapOptionsFromLegacyShape = (plan) => {
  const packages = Array.isArray(plan?.packages) ? plan.packages : [];

  return packages.flatMap((pkg) => {
    const prices = Array.isArray(pkg?.package_prices) ? pkg.package_prices : [];

    return prices.map((price) => {
      const frequency = normalizeFrequencyKey(price?.price_frequency);

      return {
        id: price?.id,
        plan_id: plan?.id,
        plan_name: plan?.name,
        package_id: pkg?.id,
        package_name: pkg?.name,
        package_description: pkg?.description,
        frequency,
        display_frequency: getDisplayFrequency(frequency),
        interval_count: price?.interval ?? null,
        interval_unit:
          frequency === 'monthly'
            ? 'month'
            : frequency === 'yearly'
              ? 'year'
              : null,
        price: Number(price?.price ?? 0),
        activation_fees: price?.activation_fees,
        is_prepaid: Boolean(price?.is_prepaid),
        is_recurring: frequency === 'monthly' || frequency === 'yearly',
        status: price?.status,
        description: price?.description,
        stripe_price_id: price?.stripe_price_id,
      };
    });
  });
};

export const getPlanOptions = (plan, config = {}) => {
  const { includePrepaid = true } = config;
  let options = [];

  if (Array.isArray(plan?.pricing_groups)) {
    options = plan.pricing_groups.flatMap((group) => {
      const groupOptions = Array.isArray(group?.options) ? group.options : [];
      return groupOptions.map((option) => mapOptionFromNewShape(plan, option));
    });
  } else {
    options = mapOptionsFromLegacyShape(plan);
  }

  if (!includePrepaid) {
    options = options.filter((option) => !option.is_prepaid);
  }

  return options;
};

export const getPlanOptionsByFrequency = (
  plan,
  frequencyValue,
  config = {}
) => {
  const { includePrepaid = true, includeLifetimeFallback = true } = config;
  const target = normalizeFrequencyKey(frequencyValue);
  const options = getPlanOptions(plan, { includePrepaid });

  return options.filter((option) => {
    if (!target) {
      return true;
    }

    if (option.frequency === target) {
      return true;
    }

    return includeLifetimeFallback && option.frequency === 'lifetime';
  });
};

export const getPrimaryPlanOption = (plan, frequencyValue, config = {}) => {
  const { includePrepaid = true, includeLifetimeFallback = true } = config;
  const target = normalizeFrequencyKey(frequencyValue);
  const options = getPlanOptionsByFrequency(plan, target, {
    includePrepaid,
    includeLifetimeFallback,
  });

  if (!options.length) {
    return null;
  }

  const exactMatch = options.find(
    (option) => option.frequency === target && option.status === 'active'
  );
  if (exactMatch) {
    return exactMatch;
  }

  const activeOption = options.find((option) => option.status === 'active');
  if (activeOption) {
    return activeOption;
  }

  return options[0];
};

export const getPlanSummaryPrice = (plan, frequencyValue) => {
  const key = normalizeFrequencyKey(frequencyValue);

  if (plan?.pricing_summary && key && plan.pricing_summary[key] !== undefined) {
    const summaryValue = plan.pricing_summary[key];
    return summaryValue === null ? null : Number(summaryValue);
  }

  const option = getPrimaryPlanOption(plan, key, {
    includePrepaid: true,
    includeLifetimeFallback: false,
  });

  return option ? Number(option.price) : null;
};

export const getFallbackPlanImage = (plan) => {
  const options = getPlanOptions(plan, { includePrepaid: true });
  const hasOnlyLifetime =
    options.length > 0 &&
    options.every((option) => option.frequency === 'lifetime');

  if (hasOnlyLifetime) {
    return '../images/Caliber_Cards_Lifetime.png';
  }

  return '../images/Caliber_Cards_Card.png';
};

export const resolveOptionStripeId = (option) => {
  return option?.stripe_price_id || option?.id;
};

export const buildBillingOption = (plan, option) => {
  if (!plan || !option) {
    return null;
  }

  return {
    planName: plan?.name,
    id: plan?.id,
    planPrice: Number(option?.price ?? 0),
    priceFrequency:
      option?.display_frequency || getDisplayFrequency(option?.frequency),
    stripeId: resolveOptionStripeId(option),
  };
};

export const getFirstPrepaidOption = (plans = []) => {
  for (const plan of plans) {
    const prepaidOption = getPlanOptions(plan, { includePrepaid: true }).find(
      (option) => option.is_prepaid
    );

    if (prepaidOption) {
      return {
        plan,
        option: prepaidOption,
      };
    }
  }

  return null;
};
